import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';

import { ProductComponent } from '@mewa/types';
import { Line } from '@mewa/ui';

import { TeaserLink } from '../types';
import DataGrid, { DataGridContainer } from './data-grid';
import { showComponents, showMaterial, showWeight } from './data-helper';
import InlineConsultationTeaser from './inline-consultation-teaser';

export type AccordionData = {
  overview: {
    summary: string;
    keyFacts: {
      title: string;
      items: string[];
    };
    weightInG: {
      title: string;
      value: string;
    };
    sizes?: {
      title: string;
      value: string;
    };
    industries: {
      title: string;
      items: string[];
    };
  };
  productFeatures: {
    summary: string;
    components: ProductComponent[] | undefined;
  };
  material: {
    summary: string;
    rows: {
      title: string;
      value: string;
    }[];
  };
};

export const DataAccordion = ({
  contactLink,
  data: { overview, productFeatures, material },
}: {
  contactLink: TeaserLink;
  data: AccordionData;
}) => {
  const hasNoData =
    !showWeight(overview.weightInG.value) &&
    !showComponents(productFeatures?.components) &&
    !showMaterial(material.rows[0].value);

  return (
    <DataAccordionContainer>
      {showWeight(overview.weightInG.value) && (
        <Accordion>
          <AccordionSummary>{overview.summary}</AccordionSummary>
          <AccordionDetails>
            <DataGrid
              title={overview.keyFacts.title}
              items={overview.keyFacts.items}
            />
            <DataGrid
              title={overview.weightInG.title}
              items={[overview.weightInG.value]}
            />
            {overview.sizes && (
              <DataGrid
                title={overview.sizes.title}
                items={[overview.sizes.value]}
              />
            )}
            <DataGrid
              title={overview.industries.title}
              items={overview.industries.items}
            />
            <DataGridContainer>
              <InlineConsultationTeaser
                data-pagefind-ignore
                description={contactLink.description}
                onClick={contactLink.onClick}
                buttonLabel={contactLink.buttonLabel}
                thumbnailKey={'consultation'}
              />
            </DataGridContainer>
          </AccordionDetails>
        </Accordion>
      )}
      {showComponents(productFeatures?.components) && (
        <Accordion>
          <AccordionSummary>{productFeatures.summary}</AccordionSummary>
          <AccordionDetails>
            {productFeatures.components.map((component) => (
              <DataGrid
                key={component.title}
                title={component.title}
                items={component.items}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}
      {showMaterial(material.rows[0].value) && (
        <Accordion>
          <AccordionSummary>{material.summary}</AccordionSummary>
          <AccordionDetails>
            <DataGrid
              title={material.rows[0].title}
              items={[material.rows[0].value]}
            />
          </AccordionDetails>
        </Accordion>
      )}
      {hasNoData && <Line />}
    </DataAccordionContainer>
  );
};

const DataAccordionContainer = styled('div')(({ theme }) => ({
  marginTop: '50px',

  [theme.breakpoints.only('mdSm')]: {
    marginTop: '80px',
  },
}));
